import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * Reusable Back button.
 *
 * Uses browser history (`navigate(-1)`) so that React Router's history-state
 * is preserved — that means scroll position, filters stored in URL query
 * strings, selected tabs (via `?tab=`), and form input held in component
 * state on the previous page all come back exactly as the user left them.
 *
 * Falls back to a sensible role-aware home when there is no previous entry
 * in history (deep-link / page reload).
 *
 * Hidden automatically on the three role dashboards so it never appears on
 * a "home" page where Back wouldn't make sense.
 */
export default function BackButton({ fallback, className }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Hide on role dashboards — these are the natural "home" pages.
  const path = location.pathname.replace(/\/+$/, '');
  const isHome = path === '/admin' || path === '/employee' || path === '/supervisor';
  if (isHome) return null;

  const resolveFallback = () => {
    if (fallback) return fallback;
    if (path.startsWith('/admin')) return '/admin';
    if (path.startsWith('/supervisor')) return '/supervisor';
    if (path.startsWith('/employee')) return '/employee';
    return '/';
  };

  const handleClick = () => {
    // history.state has an `idx` counter that React Router maintains. If
    // it's 0 the user landed here directly (no previous page in this tab),
    // so going back via the browser would either reload the same page or
    // leave the app entirely. Fall through to the role home instead.
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(resolveFallback(), { replace: true });
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleClick}
      data-testid="back-button"
      className={cn(
        'gap-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-sm h-8 px-2 mb-2',
        className
      )}
    >
      <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-xs uppercase tracking-wider">Back</span>
    </Button>
  );
}
